// Where a conversation's latest message is, shared by the Scroll to latest chip and the
// jump there when a conversation opens.
import { useEffect, type RefObject } from "react";
import { useLocation } from "react-router-dom";

// The gap left between the last item and the bar once scrolled to it.
const GAP_PX = 16;

// The transcript scrolls itself on a phone (the run pages' panel), the window otherwise.
const scrollerOf = (content: HTMLElement): HTMLElement | Window =>
  ["auto", "scroll"].includes(getComputedStyle(content).overflowY) ? content : window;

/** How far the transcript's last item runs below the top of the pinned bar, or the foot
 * of the view where a conversation has no bar (ended or hidden). Measured on the items,
 * not the page: the page is at least a window tall, so its foot says nothing about where
 * the conversation ends. */
export const hiddenBy = (content: HTMLElement, bar: HTMLElement | null): number => {
  const bottoms = Array.from(content.children, (child) => child.getBoundingClientRect().bottom);
  const last = bottoms.length ? Math.max(...bottoms) : 0;
  const scroller = scrollerOf(content);
  const limit = bar
    ? bar.getBoundingClientRect().top
    : scroller === window
      ? window.innerHeight
      : content.getBoundingClientRect().bottom;
  return last - (limit - GAP_PX);
};

export const toLatest = (
  content: HTMLElement,
  bar: HTMLElement | null,
  behavior: ScrollBehavior,
) => {
  const by = hiddenBy(content, bar);
  if (by > 0) scrollerOf(content).scrollBy({ top: by, behavior });
};

/** How far down the window a pinned header covers (ProjectHubLayout publishes it). */
export const pinnedTop = (): number =>
  parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--pinned-top")) || 0;

const scrollParentOf = (el: HTMLElement): HTMLElement | Window => {
  for (let at = el.parentElement; at; at = at.parentElement) {
    if (
      ["auto", "scroll"].includes(getComputedStyle(at).overflowY) &&
      at.scrollHeight > at.clientHeight
    )
      return at;
  }
  return window;
};

/** Puts the New divider at the top of what the reader sees: under the pinned header
 * when the window scrolls, at the top of the docked pane when the pane does. */
export const toFirstUnread = (divider: HTMLElement) => {
  const scroller = scrollParentOf(divider);
  // A scrolling panel's top padding (a phone's room for the menu button) covers it too.
  const ceiling =
    scroller === window
      ? pinnedTop()
      : (scroller as HTMLElement).getBoundingClientRect().top +
        parseFloat(getComputedStyle(scroller as HTMLElement).paddingTop);
  const by = divider.getBoundingClientRect().top - ceiling - GAP_PX;
  if (Math.abs(by) > 1) scroller.scrollBy({ top: by, behavior: "auto" });
};

// Images, review comments and the web font land after the first jump and grow the
// transcript, so the jump is repeated while it settles.
const SETTLE_MS = 5000;
// The reader taking over the scroll ends the settling early.
const READER_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

/** Opens each conversation at its latest message, or with its New divider at the top
 * when it has unread replies, unless the link names an item to land on
 * (useScrollToHash). Only while it opens: a poll or a later hash change must not pull
 * the reader down. */
export const useOpenAtLatest = ({
  contentRef,
  barRef,
  conversationId,
  ready,
}: {
  contentRef: RefObject<HTMLElement>;
  barRef: RefObject<HTMLElement>;
  conversationId: string;
  /** The transcript has loaded and rendered. */
  ready: boolean;
}) => {
  const { hash } = useLocation();
  useEffect(() => {
    const content = contentRef.current;
    if (!ready || hash || !content) return;
    // The divider arrives after the first jump (once the open is recorded), so each
    // settle step looks for it again.
    const follow = () => {
      const divider = content.querySelector<HTMLElement>("[data-first-unread]");
      if (divider) toFirstUnread(divider);
      else toLatest(content, barRef.current, "auto");
    };
    // A frame on, once the transcript has laid out.
    const frame = window.requestAnimationFrame(follow);
    const grows = new ResizeObserver(follow);
    const watch = () => {
      grows.disconnect();
      for (const child of Array.from(content.children)) grows.observe(child);
    };
    watch();
    const added = new MutationObserver(watch);
    added.observe(content, { childList: true });
    const stop = () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      grows.disconnect();
      added.disconnect();
      for (const type of READER_EVENTS) window.removeEventListener(type, stop, true);
    };
    const timer = window.setTimeout(stop, SETTLE_MS);
    for (const type of READER_EVENTS) {
      window.addEventListener(type, stop, { capture: true, passive: true });
    }
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, ready]);
};
